import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from './api';
import { queryKeys } from './queryClient';
import type {
  DashboardSummary,
  UpcomingRenewal,
  UpcomingMilestone,
  SpendTrend,
  ContractsByStatus,
  LicenseUtilization,
  SpendByVendor,
  SpendByDepartment,
  RenewalForecast,
  RenewalForecastGrouping,
  CostPerUserItem,
} from '../types';

/**
 * Dashboard Service
 *
 * Provides API functions and TanStack Query hooks for fetching dashboard data.
 * All hooks include proper type safety, caching, and error handling.
 */

/**
 * Fetch dashboard summary statistics
 *
 * @returns Promise resolving to dashboard summary data
 */
const fetchDashboardSummary = async (): Promise<DashboardSummary> => {
  return api.get<DashboardSummary>('/dashboard/summary');
};

/**
 * Fetch upcoming contract renewals
 *
 * @param days - Number of days to look ahead (default: 90)
 * @returns Promise resolving to array of upcoming renewals
 */
const fetchUpcomingRenewals = async (days: number = 90): Promise<UpcomingRenewal[]> => {
  return api.get<UpcomingRenewal[]>(`/dashboard/upcoming-renewals?days=${days}`);
};

/**
 * Fetch spending trends over time
 *
 * @param months - Number of months of historical data (default: 12)
 * @returns Promise resolving to array of monthly spending data
 */
const fetchSpendTrends = async (months: number = 12): Promise<SpendTrend[]> => {
  return api.get<SpendTrend[]>(`/dashboard/spend-trends?months=${months}`);
};

/**
 * Fetch contract distribution by status
 *
 * @returns Promise resolving to array of contract status counts
 */
const fetchContractsByStatus = async (): Promise<ContractsByStatus[]> => {
  return api.get<ContractsByStatus[]>('/dashboard/contracts-by-status');
};

/**
 * Fetch license utilization statistics
 *
 * @returns Promise resolving to license utilization data
 */
const fetchLicenseUtilization = async (): Promise<LicenseUtilization> => {
  return api.get<LicenseUtilization>('/dashboard/license-utilization');
};

/**
 * Hook: Use Dashboard Summary
 *
 * Fetches and caches dashboard summary statistics including total active contracts,
 * annual value, compliance rate, and expiring contracts.
 *
 * @returns Query result containing dashboard summary data
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useDashboardSummary();
 *
 * if (isLoading) return <Skeleton />;
 * if (error) return <Error />;
 *
 * return <div>Total: {data.totalActiveContracts}</div>;
 * ```
 */
export const useDashboardSummary = () => {
  return useQuery({
    queryKey: queryKeys.dashboard.summary(),
    queryFn: fetchDashboardSummary,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook: Use Upcoming Renewals
 *
 * Fetches contracts that are expiring within the specified number of days.
 * Useful for displaying renewal alerts and upcoming action items.
 *
 * @param days - Number of days to look ahead (default: 90)
 * @returns Query result containing array of upcoming renewals
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useUpcomingRenewals(60);
 *
 * return (
 *   <Table>
 *     {data?.map(renewal => (
 *       <Row key={renewal.contractId}>
 *         {renewal.contractName} - {renewal.daysUntil} days
 *       </Row>
 *     ))}
 *   </Table>
 * );
 * ```
 */
export const useUpcomingRenewals = (days: number = 90) => {
  return useQuery({
    queryKey: queryKeys.dashboard.renewals(days),
    queryFn: () => fetchUpcomingRenewals(days),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook: Use Spend Trends
 *
 * Fetches monthly spending data for the specified number of months.
 * Used to render line charts showing spending trends over time.
 *
 * @param months - Number of months of historical data (default: 12)
 * @returns Query result containing array of monthly spend data
 *
 * @example
 * ```tsx
 * const { data } = useSpendTrends(12);
 *
 * return (
 *   <LineChart
 *     data={data}
 *     xField="month"
 *     yField="amount"
 *   />
 * );
 * ```
 */
export const useSpendTrends = (months: number = 12) => {
  return useQuery({
    queryKey: queryKeys.dashboard.spendTrends(months),
    queryFn: () => fetchSpendTrends(months),
    staleTime: 1000 * 60 * 10, // 10 minutes (less frequently changing)
  });
};

/**
 * Hook: Use Contracts By Status
 *
 * Fetches the distribution of contracts across different statuses.
 * Used to render pie charts showing contract status breakdown.
 *
 * @returns Query result containing array of status counts
 *
 * @example
 * ```tsx
 * const { data } = useContractsByStatus();
 *
 * return (
 *   <PieChart
 *     data={data}
 *     labelField="status"
 *     valueField="count"
 *   />
 * );
 * ```
 */
export const useContractsByStatus = () => {
  return useQuery({
    queryKey: queryKeys.dashboard.contractsByStatus(),
    queryFn: fetchContractsByStatus,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook: Use License Utilization
 *
 * Fetches aggregated license utilization statistics including total purchased,
 * allocated, over-allocation, and under-utilization metrics.
 *
 * @returns Query result containing license utilization data
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useLicenseUtilization();
 *
 * if (isLoading) return <Skeleton />;
 *
 * return (
 *   <div>
 *     <LinearProgress
 *       value={(data.totalAllocated / data.totalPurchased) * 100}
 *     />
 *     <Typography>
 *       {data.totalAllocated} / {data.totalPurchased} allocated
 *     </Typography>
 *   </div>
 * );
 * ```
 */
export const useLicenseUtilization = () => {
  return useQuery({
    queryKey: queryKeys.dashboard.licenseUtilization(),
    queryFn: fetchLicenseUtilization,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

const fetchSpendByVendor = async (top: number = 10): Promise<SpendByVendor[]> => {
  return api.get<SpendByVendor[]>(`/dashboard/spend-by-vendor?top=${top}`);
};

const fetchSpendByDepartment = async (): Promise<SpendByDepartment[]> => {
  return api.get<SpendByDepartment[]>('/dashboard/spend-by-department');
};

export const useSpendByVendor = (top: number = 10) => {
  return useQuery({
    queryKey: queryKeys.dashboard.spendByVendor(top),
    queryFn: () => fetchSpendByVendor(top),
    staleTime: 1000 * 60 * 5,
  });
};

export const useSpendByDepartment = () => {
  return useQuery({
    queryKey: queryKeys.dashboard.spendByDepartment(),
    queryFn: fetchSpendByDepartment,
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Fetch contract milestones (ContractDate rows on active contracts) due within the window
 *
 * @param days - Number of days to look ahead (default: 90)
 */
const fetchUpcomingMilestones = async (days: number = 90): Promise<UpcomingMilestone[]> => {
  return api.get<UpcomingMilestone[]>(`/dashboard/upcoming-milestones?days=${days}`);
};

/**
 * Hook: Use Upcoming Milestones
 *
 * Milestones dated from today through `days` days out on active, non-archived contracts,
 * ordered by date. Backs the dashboard's Upcoming Milestones card.
 *
 * @param days - Number of days to look ahead (default: 90)
 */
export const useUpcomingMilestones = (days: number = 90) => {
  return useQuery({
    queryKey: queryKeys.dashboard.milestones(days),
    queryFn: () => fetchUpcomingMilestones(days),
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Fetch the renewal forecast: contracts renewing or expiring per fiscal month or quarter
 *
 * @param months - Months to look ahead (1 to 36; quarter grouping covers whole quarters)
 * @param groupBy - 'month' or 'quarter'
 */
const fetchRenewalForecast = async (months: number, groupBy: RenewalForecastGrouping): Promise<RenewalForecast> => {
  return api.get<RenewalForecast>(`/dashboard/renewal-forecast?months=${months}&groupBy=${groupBy}`);
};

/**
 * Hook: Use Renewal Forecast
 *
 * Every period in the window with its contracts, so the chart, the drill-down and the CSV
 * export read one query. Keeps the previous data while re-grouping so the chart does not blank.
 *
 * @param months - Months to look ahead (default: 12)
 * @param groupBy - 'month' (default) or 'quarter'
 */
export const useRenewalForecast = (months: number = 12, groupBy: RenewalForecastGrouping = 'month') => {
  return useQuery({
    queryKey: queryKeys.dashboard.renewalForecast(months, groupBy),
    queryFn: () => fetchRenewalForecast(months, groupBy),
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
  });
};

/**
 * Fetch the in-force contracts with the highest annual value per seat
 *
 * @param top - Number of rows (default: 10)
 */
const fetchCostPerUser = async (top: number = 10): Promise<CostPerUserItem[]> => {
  return api.get<CostPerUserItem[]>(`/dashboard/cost-per-user?top=${top}`);
};

/**
 * Hook: Use Cost Per User
 *
 * In-force, unarchived contracts that have seat data, most expensive per user first. Backs the
 * dashboard's Most Expensive per User card.
 *
 * @param top - Number of rows (default: 10)
 */
export const useCostPerUser = (top: number = 10) => {
  return useQuery({
    queryKey: queryKeys.dashboard.costPerUser(top),
    queryFn: () => fetchCostPerUser(top),
    staleTime: 1000 * 60 * 5,
  });
};
